/**
 * Central launch + company configuration.
 *
 * CURRENT STATE: public launch. KittyPot is live on Google Play. The iPhone
 * version is with Apple for review.
 *
 * Store availability is derived from the URLs below, NOT from a separate flag.
 * A store with an empty URL is treated as "not yet available" everywhere on the
 * site: no dead link is rendered, and the copy says it is coming soon.
 *
 * WHEN APPLE APPROVES: paste the App Store URL into APP_STORE_URL. Every
 * "coming soon to iPhone" line disappears and the iPhone download button
 * appears automatically. No other change is needed.
 *
 * See docs/internal-pre-launch-checklist.md for the outstanding legal and
 * feature-verification items. That file is internal and is not rendered.
 */

export type LaunchState = 'pre-launch' | 'public-launch'

export const DEFAULT_LAUNCH_STATE: LaunchState = 'public-launch'

/** Live store URLs. An empty string means "not released on that platform yet". */
export const APP_STORE_URL = ''
export const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.kittypot.app'

/**
 * Official store badge artwork, downloaded from Apple's Marketing Resources and
 * Google Play's Brand Guidelines. Home-made lookalikes breach both sets of
 * brand guidelines, so these must be the official assets — leave empty until
 * they are in place.
 */
export const STORE_BADGE_ASSETS = {
  appStoreBadgeSrc: '',
  googlePlayBadgeSrc:
    'https://cdn.magicpatterns.com/uploads/mYBp9GqvwTioqST4Y5pr1R/google-play-store-badge-official-download-button-icon-english-version-free-png.webp',
}

/**
 * Some official badge downloads ship on a large square canvas with transparent
 * padding around the artwork. Rendering those by height alone makes the badge
 * look tiny, so each asset can declare where the artwork actually sits inside
 * its canvas, as fractions of the image's own width/height. The badge is then
 * cropped to that region and scaled to the required height.
 *
 * Use null when the asset is already tightly cropped.
 */
export type BadgeCrop = {
  top: number
  left: number
  width: number
  height: number
}

export const STORE_BADGE_CROPS: {
  appStore: BadgeCrop | null
  googlePlay: BadgeCrop | null
} = {
  appStore: null,
  // Square canvas: artwork occupies the middle band.
  googlePlay: { top: 0.354, left: 0.023, width: 0.956, height: 0.279 },
}

export const SITE_URL = 'https://www.kittypot.com'

/** Browser companion — join a kitty from an invite without installing the app. */
export const WEB_JOIN_URL = 'https://join.kittypot.com'

export const SUPPORT_EMAIL = 'support@kittypot.com'

export const COMPANY = {
  legalName: 'KittyPot Ltd',
  brandName: 'KittyPot',
  registrationNumber: '13695043',
  /** Registered office. Confirmed against Companies House. */
  addressLines: ['KittyPot Ltd', 'Minavon', 'Cardiff CF14 6BE'],
  copyrightYear: 2026,
}

/**
 * Master switch for non-essential technologies (analytics / marketing).
 *
 * CURRENT STATE: false. The website loads NO analytics or marketing scripts at
 * all, sets only strictly necessary storage, and the consent banner is hidden.
 *
 * Setting this to true does two things and only two things:
 *  1. Shows the consent banner (components/CookieBanner.tsx).
 *  2. Switches the Cookie Policy's analytics section to the "with consent"
 *     wording.
 *
 * It does NOT itself load any script, because there are no scripts to load yet.
 * When you add analytics, add the loader inside loadConsentedScripts() in
 * utils/consent.ts — that function is the single gate and is only ever called
 * after consent is granted.
 */
export const USES_NON_ESSENTIAL_COOKIES = false
